import { createAsyncThunk } from "@reduxjs/toolkit";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { MemberDeletionRequestResponse } from "../contracts/MemberDeletionRequestResponse";
import { MemberErasureResponse } from "../../members/contracts/MemberErasureResponse";

interface PaginatedDeletionRequests {
  items: MemberDeletionRequestResponse[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export const fetchDeletionRequests = createAsyncThunk<
  PaginatedDeletionRequests,
  { page?: number; pageSize?: number },
  { rejectValue: ApiErrorResponse }
>("deletionRequests/fetch", async ({ page = 1, pageSize = 20 }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<PaginatedDeletionRequests>(
      "/member-deletion-requests",
      { params: { page, pageSize } },
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

/** The erased request, how many training records are kept under legal hold, and the latest deletion date. */
export interface ErasedDeletionRequest {
  requestId: string;
  heldEnrolments: number;
  heldUntil: string | null;
}

export const eraseDeletionRequest = createAsyncThunk<
  ErasedDeletionRequest,
  string,
  { rejectValue: ApiErrorResponse }
>("deletionRequests/erase", async (requestId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<MemberErasureResponse>(
      `/member-deletion-requests/${requestId}/erase`,
    );
    const hold = response?.data?.trainingLegalHold;
    return { requestId, heldEnrolments: hold?.heldEnrolments ?? 0, heldUntil: hold?.heldUntil ?? null };
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});

export const dismissDeletionRequest = createAsyncThunk<
  string,
  string,
  { rejectValue: ApiErrorResponse }
>("deletionRequests/dismiss", async (requestId, { rejectWithValue }) => {
  try {
    await axiosInstance.post(`/member-deletion-requests/${requestId}/dismiss`);
    return requestId;
  } catch (error) {
    return rejectWithValue(toApiError(error));
  }
});
